import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import style from './style.module.scss'; // won't generate a '.d.ts' file until component is included in the app
import { StepperSize } from 'design-v2/common/BidirectionalStepper/types';
import moment, { Moment } from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { Button, makeStyles } from '@material-ui/core';
import { IBookingInfoAtTime } from 'external-services/booking-availability-service/types';
import { floorplanFilterActions } from 'floorplan-v2/app/reducers/floorplanFilter/slice';
import TimerIcon from '@material-ui/icons/Timer';
import { DiaryRootState } from 'appRoot/rootReducer';
import DateUtilsService from 'external-services/date-utils-service';
import { ThunkDispatch } from '@reduxjs/toolkit';
import PeriodicUpdateService, {
  FLOORPLAN_HEADER_RENDER,
} from 'floorplan-v2/app/services/periodicUpdate';
import { getServiceTimesFromSelectedService } from 'app/reducers/service/slice';
import { refreshUpcomingBookings } from 'floorplan-v2/app/reducers/bookings/slice';
import PreventDoubleClickToZoom from 'design-v2/common/PreventDoubleClickToZoom';
import BidirectionalStepper from 'design-v2/common/BidirectionalStepper';
import FloorplanTimeSelector from 'app/floorplan-v2/FloorplanTimeSelector';
import { scheduleActions } from 'app/reducers/schedule/slice';
import { useMediaQueryMatch } from 'app/common/useMatchMediaHook/use-media-match-hook';
import classNames from 'classnames';
import { floorplanBlockoutsActions } from 'floorplan-v2/app/reducers/blockouts/slice';
import ViewActionsService from 'app/actions/view.actions';
import { blockoutTypeEnums } from 'app/common/BlockoutsByService/types';
import FloorplanHeaderService from 'floorplan-v2/app/services/FloorplanHeaderService';
import { FloorPlanState } from 'app/reducers/settings/types';
import { FloorplanV2State } from 'floorplan-v2/app/appRoot/rootReducer';
import { ITimesForDropdown } from 'floorplan-v2/app/services/FloorplanHeaderService/types';
import { BookingType, IBooking } from 'shared-types';
const NS = 'TimeChanger';

const usePrevious = (value: Date) => {
  const ref = useRef<Date>();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

interface ITimeChanger {
  handleDateChanged: (changedDate: Date) => void;
  selectedServiceIndex: number;
  selectedMoment: Moment;
  setSelectedMoment: React.Dispatch<React.SetStateAction<moment.Moment>>;
}

export default function TimeChanger({
  handleDateChanged,
  selectedServiceIndex,
  selectedMoment,
  setSelectedMoment,
}: ITimeChanger) {
  const defaultState = {
    left: false,
    right: false,
    mid: false,
  };
  const stepperSize = StepperSize.regular;
  const disableState = defaultState;

  const handleLeftClick = () => {
    handleIntervalChange(false);
  };
  const handleRightClick = () => {
    handleIntervalChange(true);
  };

  const handleMiddleClick = () => { };
  const handleUnFocus = () => { };
  const wrapperRef: any = useRef(null);

  const useStyles = makeStyles({
    setNowButton: {
      height: '37px',
      textTransform: 'none',
      '&:disabled': {
        background: '#222 !important',
        color: '#777 !important',
        borderColor: '#444 !important',

      },
    },
    dragTimeButton: {
      height: '37px',
      textTransform: 'none',
      width: 150,
      '&:disabled': {
        background: '#e89a3c !important',
        color: '#fff !important',
        borderColor: '#e89a3c !important',
      },
    },
  });

  const classes = useStyles();

  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();

  const timeZoneId = useSelector((state: DiaryRootState) => state.venueReducer.timeZoneId);
  const selectedFloorplanDate = useSelector(
    (state: DiaryRootState) => state.scheduleReducer.selectedDate
  );
  const currentServiceViews = useSelector(
    (state: DiaryRootState) => state.serviceReducer.currentServiceViews
  );
  const currentServiceTimes = useSelector(
    (state: DiaryRootState) => state.serviceReducer.currentServiceTimes
  );
  const selectedSchedule = useSelector(
    (state: DiaryRootState) => state.scheduleReducer.selectedSchedule
  );
  const selectedFloorplanTime = useSelector(
    (state: DiaryRootState) => state.floorplanFilterReducer.selectedFloorplanTime
  );
  const floorplanState = useSelector(
    (state: DiaryRootState) => state.settingReducer.floorplanState
  );

  const draggedBooking = useSelector((state: FloorplanV2State) => state.floorplanTableDragReducer.draggedBooking);
  const isDragSaveActionComplete = useSelector((state: FloorplanV2State) => state.floorplanTableDragReducer.isDragSaveActionComplete);
  const isTimeNowVal = useSelector((state: FloorplanV2State) => state.floorplanFilterReducer.isTimeNow);
  const dragBookingInfoForModal = useSelector((state: FloorplanV2State) => state.floorplanTableDragReducer.dragBookingInfoForModal);
  const dragCompleteTableId = useSelector((state: FloorplanV2State) => state.floorplanTableDragReducer.dragCompleteTableId);
  const dragToTableBookingSlot = useSelector((state: FloorplanV2State) => state.floorplanTableDragReducer.dragToTableBookingSlot);
  const isDragActionComplete = useSelector((state: FloorplanV2State) => state.floorplanTableDragReducer.isDragActionComplete);

  const [activeDraggedBooking, setActiveDraggedBooking] = useState(draggedBooking);
  const [timeNow, setToTimeNow] = useState(false);
  const [updatedAvailableTimes, setUpdatedAvailableTimes] = useState<any>([]);
  const [selectedTimeIndex, setSelectedTimeIndex] = useState<number>(-1);
  const [cloneSelectedTimeIndex, setCloneSelectedTimeIndex] = useState<number>(-1);
  const [bookingCardDraggedAndTimeChanged, setBookingCardDraggedAndTimeChanged] = useState<boolean>(false);
  const isInitialRender = useRef(true);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    
    if (isDragSaveActionComplete || !dragBookingInfoForModal) {
      // Reset time selection after drag is completed
      cloneSelectedTimeIndex === -1 ? setToNow() : handleTimeSelectionChange(cloneSelectedTimeIndex);
      dispatch(floorplanFilterActions.setPreviousSelectedFloorplanTime(null));
      setBookingCardDraggedAndTimeChanged(false)
    }
  }, [isDragSaveActionComplete, dragBookingInfoForModal]);

  // Effect to handle drag completion without table selection
  useEffect(() => {
    if (isDragActionComplete && activeDraggedBooking && activeDraggedBooking.bookingType === BookingType.Booking
      && !(dragCompleteTableId || dragToTableBookingSlot)) {
      // Reset time selection when drag is completed without table selection
      cloneSelectedTimeIndex === -1 ? setToNow() : handleTimeSelectionChange(cloneSelectedTimeIndex);
      dispatch(floorplanFilterActions.setPreviousSelectedFloorplanTime(null));
      setBookingCardDraggedAndTimeChanged(false)
    }
  }, [dragCompleteTableId, dragToTableBookingSlot, isDragActionComplete]);

  // Effect to handle dragged booking state changes
  useEffect(() => {
    if (Boolean(draggedBooking)) {
      setActiveDraggedBooking(draggedBooking);
      isTimeNowVal ? setCloneSelectedTimeIndex(-1) : setCloneSelectedTimeIndex(selectedTimeIndex);
      handleLogicForDraggedBooking(draggedBooking);
    }
  }, [draggedBooking]);

  const isDragBooking = useMemo(() => {
    return Boolean(draggedBooking) && draggedBooking.bookingType === BookingType.Booking;
  }, [draggedBooking]);

  // Handle time selection for dragged booking
  const handleLogicForDraggedBooking = (activeDraggedBooking: IBooking) => {
    if (!activeDraggedBooking?.time || !updatedAvailableTimes?.length) return;

    const bookingTime = moment(activeDraggedBooking.time);

    const findBookingTimeIndex = updatedAvailableTimes.findIndex((time: ITimesForDropdown) => {
      const timeSlot = moment(time.value);
      return timeSlot.isSame(bookingTime, 'minute');
    });

    // We shouldnt update the floorplan header to booking time if the booking time is the same as the selected floorplan time - hence saving the previous selected floorplan time
    dispatch(floorplanFilterActions.setPreviousSelectedFloorplanTime(selectedFloorplanTime));
    const bookingCardDraggedAndTimeChanged = !DateUtilsService.areDatesSameExceptSeconds(activeDraggedBooking.time, selectedFloorplanTime); // checking the dragged booking time with the selected floorplan time
    setBookingCardDraggedAndTimeChanged(bookingCardDraggedAndTimeChanged);

    if (findBookingTimeIndex !== -1) {
      handleTimeSelectionChange(findBookingTimeIndex);
    }
  }

  // Handle time interval changes (increase/decrease)
  const handleIntervalChange = (isIncrease: boolean) => {
    let changedTime = selectedFloorplanTime;
    if (selectedSchedule.boookingInterval <= 60) {
      if (isIncrease) {
        changedTime = DateUtilsService.addMinutes(selectedFloorplanTime, 60);
      } else {
        changedTime = DateUtilsService.subtractMinutes(selectedFloorplanTime, 60);
      }
    }
    const closestTimeObj = FloorplanHeaderService.getClosestValue(
      updatedAvailableTimes,
      changedTime,
      isIncrease ? 1 : -1,
      selectedFloorplanTime
    );
    if (closestTimeObj) {
      handleTimeSelectionChange(closestTimeObj.closestTimeIndex);
    }
  };

  // Handle time selection change and update state
  const handleTimeSelectionChange = (selectedTime: number) => {
    setSelectedTimeIndex(selectedTime);
    dispatch(
      floorplanFilterActions.setSelectedFloorplanTime(updatedAvailableTimes[selectedTime].value)
    );
    dispatch(refreshUpcomingBookings()); // Refresh bookings for the selected time
  };

  const isDateToday = useMemo(() => {
    return DateUtilsService.isDateToday(selectedFloorplanDate, timeZoneId);
  }, [selectedFloorplanDate, timeZoneId]);

  useEffect(() => {
    if (floorplanState === FloorPlanState.RESUMED) {
      setResumedState();
      return;
    }

    if (!selectedFloorplanDate || isDateToday) {
      setToNow();
    } else {
      setSelectedMoment(moment(selectedFloorplanDate));
      setSelectedTimeIndex(0);
    }
  }, []);

  const prevPelectedFloorplanDate = usePrevious(selectedFloorplanDate);

  useEffect(() => {
    if (
      !DateUtilsService.isDateToday(prevPelectedFloorplanDate, timeZoneId) &&
      DateUtilsService.isDateToday(selectedFloorplanDate, timeZoneId)
    ) {
      setToNow();
    }
    setSelectedTimeIndex(0);
  }, [selectedFloorplanDate, prevPelectedFloorplanDate]);

  const serviceList = useMemo(() => {
    const result = FloorplanHeaderService.getServicesList(currentServiceViews);
    return result;
  }, [currentServiceViews]);

  const isInSelectedService = useMemo(() => {
    const result = FloorplanHeaderService.isInSelectedService(
      isDateToday,
      currentServiceViews,
      selectedServiceIndex,
      timeZoneId
    );
    return result;
  }, [serviceList, selectedServiceIndex, isDateToday, timeZoneId]); //, venueTime

  useEffect(() => {
    const watchSelectedServiceIndex = async () => {
      const selectedServiceView = currentServiceViews[selectedServiceIndex];
      const { payload } = await dispatch(
        getServiceTimesFromSelectedService({
          selectedServiceView: selectedServiceIndex,
          currentServiceViews: currentServiceViews,
        })
      );

      if (isDateToday) {
        // no need to check if selected service is within now time, if day is not today
        const includesCurrentVenueTime = FloorplanHeaderService.isVenueTimeWithinAvailableTimes(
          payload as IBookingInfoAtTime[],
          timeZoneId,
          selectedServiceView
        );
        if (includesCurrentVenueTime) {
          setToTimeNow(true);
        }
      }

      dispatch(refreshUpcomingBookings());
      setSelectedTimeIndex(0);
    };
    watchSelectedServiceIndex();
  }, [selectedServiceIndex]);

  // if the floorplan has been stopped and then resumed again (eg when you open timeline or runsheet views)
  const setResumedState = () => {
    const venueTime = DateUtilsService.getVenueTime(timeZoneId);
    const isTimeNow = DateUtilsService.isDateSame(selectedFloorplanTime, venueTime, true);

    const times = FloorplanHeaderService.getTimesForDropdown(
      currentServiceTimes,
      isTimeNow,
      timeZoneId
    );
    if (isTimeNow) {
      // if time is still the same minute as the venue time, treat it as "set to now time"
      setToTimeNow(true);
      if (times?.length) {
        setSelectedMoment(moment(times[0].value));
      }
    } else {
      // if the time doesn't match the venue time, use the selectedFloorplanTime to select the closest time in the dropdown
      const closestTimeObj = FloorplanHeaderService.getClosestValue(
        times,
        selectedFloorplanTime,
        0,
        selectedFloorplanTime
      );
      if (closestTimeObj) {
        setSelectedMoment(moment(closestTimeObj.closestTimeInDropdown));
        setSelectedTimeIndex(closestTimeObj.closestTimeIndex);
      }
    }
  };

  const updateTimes = () => {
    const allowTimesUpdate = FloorplanHeaderService.doUpdateTimes(
      !!updatedAvailableTimes.length,
      selectedMoment,
      currentServiceTimes,
    );

    if (!allowTimesUpdate) {
      return;
    }


    const times = FloorplanHeaderService.getTimesForDropdown(
      currentServiceTimes,
      timeNow,
      timeZoneId
    );

    setUpdatedAvailableTimes(times);

    if (disabledSetNow) {
      setSelectedTimeIndex(0);
      runSetNowTimer();
    }
    else {
      PeriodicUpdateService.removeMainTrackFuncs(FLOORPLAN_HEADER_RENDER);
    }
  };

  useEffect(() => {
    if (currentServiceTimes) {
      updateTimes();
    }
  }, [currentServiceTimes, timeNow]);


  const updateSetNow = useCallback(
    (availableTimesVal: IBookingInfoAtTime[], isTimeNow: boolean) => {
      const times = FloorplanHeaderService.getTimesForDropdown(
        availableTimesVal,
        isTimeNow,
        timeZoneId
      );
      setUpdatedAvailableTimes(times);
    },
    [currentServiceTimes]
  );

  const handleUpdateSetNow = () => {
    setToNow();
    runSetNowTimer();
  };

  const disabledSetNow = useMemo(() => {
    const venueTime = DateUtilsService.getVenueTime(timeZoneId);
    if (!venueTime || !selectedFloorplanTime) {
      return true;
    }
    if (!isDateToday) {
      return false;
    }

    return DateUtilsService.areDatesSameExceptSeconds(venueTime, selectedFloorplanTime);
  }, [selectedFloorplanTime, isDateToday]);

  useEffect(() => {
    dispatch(floorplanFilterActions.setIsTimeNow(disabledSetNow));
  }, [disabledSetNow]);

  const setToNow = () => {
    const venueTime = DateUtilsService.getVenueTime(timeZoneId);
    setTimeout(() => {
      // for some reason this needed to be in a setTimeout to get triggered when returning from the Diary's main menu
      setSelectedMoment(moment(venueTime));
    }, 0);

    const clearDate = DateUtilsService.getClearDate(venueTime, timeZoneId);
    setSelectedTimeIndex(0);
    handleDateChanged(clearDate);
    setToTimeNow(true);
    dispatch(floorplanFilterActions.setSelectedFloorplanTime(venueTime));
    dispatch(scheduleActions.setSelectedDate(clearDate));
    dispatch(floorplanBlockoutsActions.resetActiveAddBlockouts());
    ViewActionsService.onBlockoutFinished(blockoutTypeEnums.byTable);
  };

  const runSetNowTimer = () => {
    PeriodicUpdateService.addMainTrackFuncs({
      name: FLOORPLAN_HEADER_RENDER,
      callback: () => {
        updateSetNow(currentServiceTimes, true);
      },
    });
  };

  // if current time is not in selected service, then we stop PeriodicUpdateService tracking function
  useEffect(() => {
    if (!isInSelectedService) {
      setToTimeNow(false);
      PeriodicUpdateService.removeMainTrackFuncs(FLOORPLAN_HEADER_RENDER);
    }
  }, [isInSelectedService]);

  // hook to get the size of the device screen - md is from 768 px to 1024 px
  const isSmSize = useMediaQueryMatch('sm');
  const isMdSize = useMediaQueryMatch('md');
  const isLgSize = useMediaQueryMatch('lg');

  const isSmallScreen = isSmSize || isMdSize || isLgSize;

  return (
    <>
      {selectedSchedule?.isOpen && (
        <div
          className={classNames({
            [style.childWrapper]: true,
          })}
        >
          {isDragBooking && bookingCardDraggedAndTimeChanged && ( // if the booking card is dragged and the time is changed, then we show the styled time button
            <Button
              className={classNames({
                [classes.dragTimeButton]: true
              })}
              disabled={true}
              variant="contained"
              color="primary"
            >
              {moment(draggedBooking?.time).format('h:mm A')}
            </Button>
          )}
          {(!isDragBooking || !bookingCardDraggedAndTimeChanged) && (
            <PreventDoubleClickToZoom>
              <BidirectionalStepper
                disableState={disableState}
                minWidth={isSmallScreen ? 104 : 120}
                stepperSize={stepperSize}
                handleLeftClick={handleLeftClick}
                handleRightClick={handleRightClick}
                handleMiddleClick={handleMiddleClick}
                handleUnFocus={handleUnFocus}
                enableUnFocusForMouseLeave={false}
                parentRef={wrapperRef}
                isFloorPlan={true}
              >
                <FloorplanTimeSelector
                  label={`Times`}
                  list={updatedAvailableTimes}
                  selectedItemIndex={selectedTimeIndex}
                  handleSelectItem={handleTimeSelectionChange}
                  isSmallScreen={isSmallScreen}
                  focusValue={FloorplanHeaderService.getClosestFocussedValue(
                    updatedAvailableTimes,
                    selectedFloorplanTime
                  )}
                />
              </BidirectionalStepper>
            </PreventDoubleClickToZoom>
          )}
        </div>
      )}
      {(selectedSchedule?.isOpen || !isDateToday) && (
        <div
          className={classNames({
            [style.childWrapper]: true,
            [style.nowBtn]: isMdSize || isSmSize,
          })}
        >
          <Button
            className={classNames({
              [classes.setNowButton]: true,
              [style.timerIcon]: isMdSize || isSmSize,
              [style.centerTime]: isSmallScreen,
            })}
            data-testid={disabledSetNow ? 'disabledSetNow' : ''}
            disabled={disabledSetNow}
            variant="contained"
            color="primary"
            startIcon={
              <TimerIcon
                className={classNames({
                  [style.centerTime]: isMdSize || isSmSize,
                })}
              />
            }
            onClick={handleUpdateSetNow}
          >
            {!isSmallScreen && `Set to now`}
            {isLgSize ? `Now` : null}
          </Button>
        </div>
      )}
    </>
  );
}
